import React, { useState, useEffect, useRef } from "react";
import { useQuickIsland } from "../../contexts/QuickIslandContext";
import { SearchResult } from "../../types";
import debugx from "debugx";
import { getOverpassWorkerService } from "../../services/overpassWorkerService";
import { getSearchService } from "../../services/searchService";
import { getSearchWorkerService } from "../../services/searchWorkerService";
import { getRouteService } from "../../services/routeService";
import { getTrackingService } from "../../services/trackingService";
import { calculateDistanceMiles, calculateBearing } from "../../utils/geoUtils";
import { formatDistanceWithUnit } from "../../utils/formatting";

const debug = debugx("bikedash:search-detail");

type SearchDistance = "local" | "10mi" | "25mi" | "∞";

// Persistent state for search (survives component unmount)
interface SearchState {
  query: string;
  results: SearchResult[];
  distance: SearchDistance;
  scrollPosition: number;
}

const persistentSearchState: SearchState = {
  query: "",
  results: [],
  distance: "local",
  scrollPosition: 0,
};

export const QuickIslandSearchDetail: React.FC = () => {
  const {
    closeIsland: originalCloseIsland,
    searchBusy,
    setSearchBusy,
    isSearching: isSearchingGlobal,
  } = useQuickIsland();
  const [isWaiting, setIsWaiting] = useState(false);
  const isExplicitCloseRef = useRef(false);
  const searchService = getSearchService();
  const workerService = getOverpassWorkerService();
  const routeService = getRouteService();
  const trackingService = getTrackingService();
  const [savedRoutes, setSavedRoutes] = useState<any[]>([]);
  const [nearbyTrails, setNearbyTrails] = useState<SearchResult[]>([]);
  const [mapCenter, setMapCenter] = useState<{
    lat: number;
    lon: number;
  } | null>(null);
  const isMountedRef = useRef(true);
  const displayMarkersTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const previousSearchQueryRef = useRef("");
  const [currentGpsPosition, setCurrentGpsPosition] = useState<{
    lat: number;
    lng: number;
  } | null>(null);

  // Load initial config from localStorage SYNCHRONOUSLY
  const getInitialConfig = () => {
    const stored = localStorage.getItem("cacheGridUiConfig");
    if (stored) {
      try {
        const config = JSON.parse(stored);
        return { cacheGridSize: 3, alwaysShowCacheGrid: false, ...config };
      } catch (error) {
        debug("Failed to parse stored config:", error);
      }
    }
    return { cacheGridSize: 3, alwaysShowCacheGrid: false };
  };

  const initialConfig = getInitialConfig();
  const [uiConfig, setUiConfig] = useState<{
    cacheGridSize: 3 | 5 | 7 | 9;
    alwaysShowCacheGrid: boolean;
  }>(initialConfig);
  const [cacheGridState, setCacheGridState] = useState<
    ("cached" | "failed" | "fetching" | "none")[]
  >(
    new Array(initialConfig.cacheGridSize * initialConfig.cacheGridSize).fill(
      "none",
    ),
  );

  debug(
    "QuickIslandSearchDetail: cacheGridSize=%d, cacheGridState.length=%d",
    uiConfig.cacheGridSize,
    cacheGridState.length,
  );

  // Resize cache grid state when grid size changes
  useEffect(() => {
    const expectedLength = uiConfig.cacheGridSize * uiConfig.cacheGridSize;
    if (cacheGridState.length !== expectedLength) {
      debug(
        "Resizing cacheGridState from %d to %d",
        cacheGridState.length,
        expectedLength,
      );
      setCacheGridState(new Array(expectedLength).fill("none"));
    }
  }, [uiConfig.cacheGridSize]);

  // Handle island closing with different behaviors
  const closeIsland = () => {
    const mapService = window.__bikedash_maps?.["main"];

    // Always clear temporary place overlays when search island closes
    if (mapService) {
      mapService.clearTemporaryPlaces();
    }

    // If explicitly closed with X button, clear the markers
    // If dismissed (clicking outside), keep the markers
    if (isExplicitCloseRef.current) {
      if (mapService) {
        mapService.clearSearchResults();
        debug("Cleared search markers - explicit close with X button");
      }
      isExplicitCloseRef.current = false; // Reset flag
    } else {
      debug(
        "Keeping search markers - dismissed by clicking outside or selecting result",
      );
    }
    originalCloseIsland();
  };
  // Initialize from search service state
  const serviceState = searchService.getState();
  const [searchQuery, setSearchQuery] = useState(
    serviceState.query || persistentSearchState.query,
  );
  const [searchResults, setSearchResults] = useState<SearchResult[]>(
    serviceState.results.length > 0
      ? serviceState.results
      : persistentSearchState.results,
  );
  const [isSearching, setIsSearching] = useState(serviceState.isSearching);
  const [error, setError] = useState<string | null>(null);
  const [searchDistance, setSearchDistance] = useState<SearchDistance>(
    serviceState.distance || persistentSearchState.distance,
  );
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  // Position debouncing is no longer used but UI references it
  const positionDebouncing = false;
  const isUpdatingCacheGridRef = useRef(false);

  // Update cache grid state when location changes or cache updates
  const updateCacheGridState = async () => {
    // Skip if already updating
    if (isUpdatingCacheGridRef.current) {
      return;
    }

    isUpdatingCacheGridRef.current = true;

    try {
      if (searchDistance !== "local") {
        // Only show cache grid for local searches
        setCacheGridState(
          new Array(uiConfig.cacheGridSize * uiConfig.cacheGridSize).fill(
            "none",
          ),
        );
        isUpdatingCacheGridRef.current = false;
        return;
      }

      const mapService = window.__bikedash_maps?.["main"];
      const center = mapService?.getCenter();
      if (!center) {
        isUpdatingCacheGridRef.current = false;
        return;
      }

      debug("UI map center: lat=%.6f, lon=%.6f", center.lat, center.lon);

      // Calculate grid positions (dynamic size based on config)
      const GRID_SIZE = 0.2;
      // Grid tiles are centered at EVEN tenths: ...0.0, 0.2, 0.4, 0.6, 0.8...
      const centerGridLat = Math.round(center.lat / GRID_SIZE) * GRID_SIZE;
      const centerGridLon = Math.round(center.lon / GRID_SIZE) * GRID_SIZE;

      debug(
        "UI grid center: lat %.6f -> %.1f, lon %.6f -> %.1f",
        center.lat,
        centerGridLat,
        center.lon,
        centerGridLon,
      );

      // Generate offsets based on grid size
      const halfSize = Math.floor(uiConfig.cacheGridSize / 2);
      const offsets: [number, number][] = [];
      for (let latOffset = halfSize; latOffset >= -halfSize; latOffset--) {
        for (let lonOffset = -halfSize; lonOffset <= halfSize; lonOffset++) {
          offsets.push([latOffset, lonOffset]);
        }
      }

      debug(
        "Generated %d offsets for %dx%d grid",
        offsets.length,
        uiConfig.cacheGridSize,
        uiConfig.cacheGridSize,
      );

      const tiles = offsets.map(([latOff, lonOff]) => ({
        gridLat: centerGridLat + latOff * GRID_SIZE,
        gridLon: centerGridLon + lonOff * GRID_SIZE,
      }));

      const startTime = performance.now();
      debug("UI checking %d tiles in batch", tiles.length);

      // Use batch method to check all tiles in one worker call
      const gridStates = await workerService.checkGridStatusBatch(tiles);

      const elapsed = performance.now() - startTime;
      debug("Cache grid check took %.1fms for %d tiles", elapsed, tiles.length);

      setCacheGridState(gridStates);
    } finally {
      isUpdatingCacheGridRef.current = false;
    }
  };

  // Listen for config changes from other components
  useEffect(() => {
    // Listen for config changes
    const handleConfigChange = (e: CustomEvent) => {
      const newConfig = e.detail;
      setUiConfig(newConfig);
      // Resize the cache grid state array if grid size changed
      const newSize = newConfig.cacheGridSize * newConfig.cacheGridSize;
      setCacheGridState(new Array(newSize).fill("none"));

      // Manually update cache grid with new config (don't wait for state update)
      if (searchDistance === "local") {
        const mapService = window.__bikedash_maps?.["main"];
        const center = mapService?.getCenter();
        if (!center) return;

        const GRID_SIZE = 0.2;
        const centerGridLat = Math.round(center.lat / GRID_SIZE) * GRID_SIZE;
        const centerGridLon = Math.round(center.lon / GRID_SIZE) * GRID_SIZE;

        const halfSize = Math.floor(newConfig.cacheGridSize / 2);
        const offsets: [number, number][] = [];
        for (let latOffset = halfSize; latOffset >= -halfSize; latOffset--) {
          for (let lonOffset = -halfSize; lonOffset <= halfSize; lonOffset++) {
            offsets.push([latOffset, lonOffset]);
          }
        }

        debug(
          "Config changed - fetching %d tiles for %dx%d grid",
          offsets.length,
          newConfig.cacheGridSize,
          newConfig.cacheGridSize,
        );

        Promise.all(
          offsets.map(async ([latOff, lonOff]) => {
            const gridLat = centerGridLat + latOff * GRID_SIZE;
            const gridLon = centerGridLon + lonOff * GRID_SIZE;
            return await workerService.checkGridStatus(gridLat, gridLon);
          }),
        ).then((gridStates) => {
          setCacheGridState(gridStates);
        });
      }
    };

    window.addEventListener(
      "cacheGridUiConfigChanged",
      handleConfigChange as any,
    );

    return () => {
      window.removeEventListener(
        "cacheGridUiConfigChanged",
        handleConfigChange as any,
      );
    };
  }, []);

  // Subscribe to saved routes updates
  useEffect(() => {
    const unsubscribe = routeService.subscribeSavedRoutes(() => {
      setSavedRoutes(routeService.getSavedRoutes());
    });
    return unsubscribe;
  }, [routeService]);

  // Subscribe to GPS position from tracking service
  useEffect(() => {
    const updatePosition = () => {
      const pos = trackingService.getCurrentPosition();
      if (pos?.lat && pos?.lng) {
        setCurrentGpsPosition({ lat: pos.lat, lng: pos.lng });
      }
    };

    // Update immediately
    updatePosition();

    // Subscribe to updates
    const subscription = trackingService.subscribe(updatePosition);
    return () => subscription.unsubscribe();
  }, [trackingService]);

  // Subscribe to search service updates - NO DEPENDENCIES to keep subscription stable
  useEffect(() => {
    const unsubscribe = searchService.subscribe((state) => {
      debug("Search service state updated:", state.results.length, "results");
      setSearchResults(state.results);
      setIsSearching(state.isSearching);
      setIsWaiting(state.isWaiting);
      // Don't update local query/distance from service to avoid loops
    });

    return unsubscribe;
  }, []); // Empty deps - only subscribe once on mount

  // Initialize previousSearchQueryRef on mount with the current query
  useEffect(() => {
    previousSearchQueryRef.current = searchQuery;
  }, []);

  // Track map center position for nearby trails
  useEffect(() => {
    const mapService = window.__bikedash_maps?.["main"];
    if (!mapService) return;

    // Set initial center
    const initialCenter = mapService.getCenter();
    if (initialCenter) {
      setMapCenter(initialCenter);
    }

    // Subscribe to map movement with debouncing
    let debounceTimer: NodeJS.Timeout | null = null;
    const unsubscribe = mapService.onMoveEnd(() => {
      if (debounceTimer) {
        clearTimeout(debounceTimer);
      }
      debounceTimer = setTimeout(() => {
        const center = mapService.getCenter();
        if (center) {
          setMapCenter(center);
        }
      }, 500); // 500ms debounce
    });

    return () => {
      if (debounceTimer) {
        clearTimeout(debounceTimer);
      }
      unsubscribe();
    };
  }, []);

  // Load nearby trails when map moves (only if no query and in local mode)
  useEffect(() => {
    const loadNearbyTrails = async () => {
      // Clear nearby trails and markers when user starts typing or changes mode
      if (searchQuery || searchDistance !== "local") {
        setNearbyTrails([]);

        // Clear markers only if user just started typing (transition from no query to query)
        // Don't clear if component is remounting with an existing query
        if (searchQuery && previousSearchQueryRef.current === "") {
          const mapService = window.__bikedash_maps?.["main"];
          if (mapService) {
            debug("Clearing nearby trail markers (user started typing)");
            mapService.clearSearchResults();
          }
        }

        // Update ref
        previousSearchQueryRef.current = searchQuery;
        return;
      }

      // Update ref
      previousSearchQueryRef.current = searchQuery;

      if (!mapCenter) return;

      debug(
        "Loading nearby trails at %.4f, %.4f",
        mapCenter.lat,
        mapCenter.lon,
      );

      try {
        // Fetch local bike features - trails only (filtering at worker level)
        const trails = await workerService.searchLocalBikeFeatures(
          mapCenter.lat,
          mapCenter.lon,
          3, // gridRange
          false, // onlyCached
          true, // trailsOnly - filter at worker level to avoid flash of all markers
        );

        debug("Found %d nearby trails", trails.length);

        // Format for display
        const searchWorkerService = getSearchWorkerService();
        const formattedResults = await searchWorkerService.formatForDisplay(
          trails,
          "", // No query
          mapCenter,
        );

        // Sort by distance
        const sortedResults = formattedResults.sort((a: any, b: any) => {
          const aDist = calculateDistanceMiles(
            parseFloat(a.lat),
            parseFloat(a.lon),
            mapCenter.lat,
            mapCenter.lon,
          );
          const bDist = calculateDistanceMiles(
            parseFloat(b.lat),
            parseFloat(b.lon),
            mapCenter.lat,
            mapCenter.lon,
          );
          return aDist - bDist;
        });

        // Only show if there's still no query and component is still mounted
        if (
          !searchQuery &&
          searchDistance === "local" &&
          isMountedRef.current
        ) {
          const topTrails = sortedResults.slice(0, 20);
          setNearbyTrails(topTrails);

          // Clear any pending timeout
          if (displayMarkersTimeoutRef.current) {
            clearTimeout(displayMarkersTimeoutRef.current);
          }

          // Wait a tick to ensure state is updated before displaying markers
          displayMarkersTimeoutRef.current = setTimeout(() => {
            // Double-check we're still in the right state and mounted
            if (
              !searchQuery &&
              searchDistance === "local" &&
              isMountedRef.current
            ) {
              const mapService = window.__bikedash_maps?.["main"];
              if (mapService && topTrails.length > 0) {
                debug(
                  "Displaying %d nearby trail markers on map",
                  topTrails.length,
                );
                mapService.displaySearchResults(topTrails);
              }
            }
            displayMarkersTimeoutRef.current = null;
          }, 0);
        }
      } catch (error) {
        debug.error("Failed to load nearby trails:", error);
      }
    };

    loadNearbyTrails();
  }, [searchQuery, searchDistance, mapCenter]); // Rerun when map moves or query/distance changes

  // Clear nearby trail markers and route preview when island closes
  useEffect(() => {
    isMountedRef.current = true;

    return () => {
      isMountedRef.current = false;

      // Cancel any pending marker display timeout
      if (displayMarkersTimeoutRef.current) {
        clearTimeout(displayMarkersTimeoutRef.current);
        displayMarkersTimeoutRef.current = null;
      }

      // On unmount (island closes), clear markers only if no search query
      const mapService = window.__bikedash_maps?.["main"];
      if (mapService) {
        // Only clear markers and previews if there's no search query
        // If user searched for something, leave everything visible
        if (!searchQuery) {
          debug(
            "Clearing nearby trail markers and previews on island close (no search query)",
          );
          mapService.clearSearchResults();
          mapService.clearTemporaryPlaces();
        } else {
          debug(
            "Keeping search results and trail preview visible (user has search query)",
          );
        }
      }
    };
  }, [searchQuery]);

  // Update cache grid on mount, distance change, and cache updates
  useEffect(() => {
    // Try to update immediately, but if map isn't ready, the interval will catch it
    updateCacheGridState();

    // Listen for cache updates
    const handleCacheUpdate = () => {
      updateCacheGridState();
    };

    // Listen for API state changes (suspended/resumed)
    const handleApiStateChange = () => {
      updateCacheGridState();
    };

    window.addEventListener("cacheUpdated", handleCacheUpdate);
    window.addEventListener("overpassApiStateChanged", handleApiStateChange);

    // Also update periodically to catch tile boundary crossings (every 200ms when in local mode)
    let intervalId: NodeJS.Timeout | null = null;
    if (searchDistance === "local") {
      intervalId = setInterval(() => {
        updateCacheGridState();
      }, 200);
    }

    return () => {
      window.removeEventListener("cacheUpdated", handleCacheUpdate);
      window.removeEventListener(
        "overpassApiStateChanged",
        handleApiStateChange,
      );
      if (intervalId) {
        clearInterval(intervalId);
      }
    };
  }, [searchDistance, uiConfig.cacheGridSize, currentGpsPosition]); // Update when search distance, grid size, or position changes

  // Save state to persistent storage whenever it changes
  useEffect(() => {
    persistentSearchState.query = searchQuery;
    // Only update service if it's actually different
    if (searchService.getState().query !== searchQuery) {
      searchService.setQuery(searchQuery);
    }
  }, [searchQuery, searchService]);

  useEffect(() => {
    persistentSearchState.results = searchResults;
  }, [searchResults]);

  useEffect(() => {
    persistentSearchState.distance = searchDistance;
    // Only update service if it's actually different
    if (searchService.getState().distance !== searchDistance) {
      searchService.setDistance(searchDistance);
    }
  }, [searchDistance, searchService]);

  // Restore scroll position and redisplay search markers when component mounts
  useEffect(() => {
    if (
      scrollContainerRef.current &&
      persistentSearchState.scrollPosition > 0
    ) {
      scrollContainerRef.current.scrollTop =
        persistentSearchState.scrollPosition;
    }

    // Redisplay search results on map if they exist
    // Use persistentSearchState.results to restore even if component state hasn't initialized yet
    const resultsToRestore =
      searchResults.length > 0 ? searchResults : persistentSearchState.results;
    if (resultsToRestore.length > 0) {
      const mapService = window.__bikedash_maps?.["main"];
      if (mapService) {
        mapService.displaySearchResults(resultsToRestore);
        debug(
          "Restored %d search result markers on map",
          resultsToRestore.length,
        );
      }
    }
  }, []);

  // Map click dismissal is now handled centrally in PersistentQuickIsland

  // Handle cleanup when component unmounts
  useEffect(() => {
    return () => {
      // On unmount, if it was an explicit close (X button), markers were already cleared
      // If it was a dismiss (click outside), keep the markers visible
      if (!isExplicitCloseRef.current) {
        debug("Component unmounting from dismiss - keeping search markers");
      }
    };
  }, []);

  // Save scroll position before unmounting
  useEffect(() => {
    const saveScrollPosition = () => {
      if (scrollContainerRef.current) {
        persistentSearchState.scrollPosition =
          scrollContainerRef.current.scrollTop;
      }
    };

    // Save on scroll
    const scrollContainer = scrollContainerRef.current;
    if (scrollContainer) {
      scrollContainer.addEventListener("scroll", saveScrollPosition);
    }

    // Save when closing
    return () => {
      saveScrollPosition();
      if (scrollContainer) {
        scrollContainer.removeEventListener("scroll", saveScrollPosition);
      }
    };
  }, []);

  // Removed - search service handles getting map center and calculating bounds

  // Removed initial search tracking - handled by service

  const handleResultPan = async (result: SearchResult) => {
    debug("Panning to location:", result.display_name, result.lat, result.lon);

    const mapService = window.__bikedash_maps?.["main"];
    if (!mapService) return;

    const lat = parseFloat(result.lat);
    const lon = parseFloat(result.lon);

    // Try to fetch geometry to calculate proper bounds
    let osmType: string | undefined;
    let osmId: string | undefined;

    const placeIdStr = String(result.place_id);
    if (placeIdStr.startsWith("osm_")) {
      const parts = placeIdStr.split("_");
      osmType = parts[1];
      osmId = parts.slice(2).join("_");
    } else if (result.osm_type && result.osm_id) {
      osmType = result.osm_type;
      osmId = String(result.osm_id);
    }

    if (osmType && osmId) {
      try {
        const overpassWorker = getOverpassWorkerService();
        const cachedData = await overpassWorker.searchLocalBikeFeatures(
          lat,
          lon,
          3,
          true,
        );

        const numericId = String(osmId);
        const targetElement = cachedData.find((e: any) => {
          const matches =
            e.id?.toString() === numericId ||
            e.id?.toString() === osmId ||
            `osm_${e.type}_${e.id}` === `osm_${osmType}_${osmId}`;
          return matches && e.type === osmType;
        });

        if (targetElement) {
          let displayElements: any[] = [];

          if (targetElement.type === "relation" && targetElement.members) {
            const memberWayIds = new Set<number>();
            for (const member of targetElement.members) {
              if (member.type === "way") {
                memberWayIds.add(
                  typeof member.ref === "number"
                    ? member.ref
                    : Number(member.ref),
                );
              }
            }

            const memberWays = cachedData.filter(
              (e: any) =>
                e.type === "way" &&
                e.id !== undefined &&
                memberWayIds.has(Number(e.id)),
            );

            if (memberWays.length > 0) {
              displayElements = mapService.findConnectedSegmentsForRelation(
                memberWays,
                cachedData,
                targetElement.tags?.name,
              );
            } else {
              displayElements = [targetElement];
            }
          } else if (targetElement.type === "way") {
            displayElements = mapService.findConnectedSegments(
              targetElement,
              cachedData,
            );
          } else {
            displayElements = [targetElement];
          }

          // Display geometry as temporary place overlay (for parks only)
          if (displayElements.length > 0) {
            // Show the park on map in temporary place layer
            mapService.displayTemporaryPlace(displayElements, overpassWorker);
            return; // displayTemporaryPlace handles fitting bounds
          }
        }
      } catch (err) {
        debug("Failed to fetch geometry for bounds:", err);
      }
    }

    // Fallback: use bounding box if available
    if (result.boundingbox && result.boundingbox.length === 4) {
      const bounds = {
        minLat: parseFloat(result.boundingbox[0]),
        maxLat: parseFloat(result.boundingbox[1]),
        minLon: parseFloat(result.boundingbox[2]),
        maxLon: parseFloat(result.boundingbox[3]),
      };

      const latDiff = bounds.maxLat - bounds.minLat;
      const lonDiff = bounds.maxLon - bounds.minLon;

      if (latDiff > 0.0001 && lonDiff > 0.0001) {
        mapService.fitBounds(bounds);
        return;
      }
    }

    // Final fallback: fit to a 1-mile box around the point
    const mileInDegrees = 1 / 69;
    mapService.fitBounds({
      minLat: lat - mileInDegrees,
      maxLat: lat + mileInDegrees,
      minLon: lon - mileInDegrees,
      maxLon: lon + mileInDegrees,
    });
  };

  const handleResultPreview = async (
    result: SearchResult,
    e: React.MouseEvent,
  ) => {
    e.stopPropagation(); // Prevent triggering the pan
    debug("Previewing location:", result.display_name, result.lat, result.lon);

    // Close island immediately for instant feedback
    closeIsland();

    // Set search busy state
    setSearchBusy(true);

    try {
      const mapService = window.__bikedash_maps?.["main"];
      if (mapService) {
        // First set mode to "free" so map can pan to the location
        mapService.setMode("free");

        // Rotate to north (0 degrees)
        mapService.setRotation(0);

        // Don't pan here - let displaySelectedFeature handle all positioning

        // Small delay to allow UI to update and spinner to start animating smoothly
        await new Promise((resolve) => setTimeout(resolve, 100));

        // Now do the heavy processing
        // Try to fetch and display geometry for OSM features
        let geometryFetched = false;

        // Check if we have OSM info either from place_id format or osm_type/osm_id fields
        let osmType: string | undefined;
        let osmId: string | undefined;

        // Convert place_id to string if it's a number
        const placeIdStr = String(result.place_id);

        if (placeIdStr.startsWith("osm_")) {
          // Local search results have OSM info in place_id
          const parts = placeIdStr.split("_");
          osmType = parts[1]; // node, way, or relation
          osmId = parts.slice(2).join("_"); // Join in case ID has underscores
        } else if (result.osm_type && result.osm_id) {
          // Nominatim results have separate fields
          osmType = result.osm_type;
          osmId = String(result.osm_id); // Ensure osm_id is a string too
        }

        if (osmType && osmId) {
          debug("Looking for geometry for OSM feature:", osmType, osmId);

          try {
            const lat = parseFloat(result.lat);
            const lon = parseFloat(result.lon);

            // Get cached data from worker - this includes member ways for relations
            const overpassWorker = getOverpassWorkerService();
            debug(
              "🟢 ISLAND RESULT CLICK: Fetching cached data at lat=%f, lon=%f",
              lat,
              lon,
            );
            // Query with onlyCached: true to prevent scheduling more tile fetches
            const cachedData = await overpassWorker.searchLocalBikeFeatures(
              lat,
              lon,
              3, // gridRange: 3 for 3x3 grid
              true, // onlyCached: true - don't schedule more fetches
            );
            debug(
              "🟢 ISLAND RESULT CLICK: Got %d cached elements from worker",
              cachedData.length,
            );

            // Find the target element in cache by ID
            const numericId = String(osmId);
            debug(
              "Looking for element: osmType=%s, osmId=%s, numericId=%s",
              osmType,
              osmId,
              numericId,
            );
            debug("Cache has %d elements", cachedData.length);

            const targetElement = cachedData.find((e: any) => {
              const matches =
                e.id?.toString() === numericId ||
                e.id?.toString() === osmId ||
                `osm_${e.type}_${e.id}` === `osm_${osmType}_${osmId}`;
              return matches && e.type === osmType;
            });

            if (!targetElement) {
              debug("ERROR: Target element not found in cache!");
              debug("Searched for: type=%s, id=%s", osmType, numericId);
              const sampleRelations = cachedData
                .filter((e: any) => e.type === "relation")
                .slice(0, 5);
              debug(
                "Sample relations in cache: %o",
                sampleRelations.map((r: any) => ({
                  id: r.id,
                  name: r.tags?.name,
                })),
              );
              geometryFetched = false;
            } else {
              debug(
                "Found target element: type=%s, id=%s, name=%s, hasMembersArray=%s, membersCount=%d",
                targetElement.type,
                targetElement.id,
                targetElement.tags?.name,
                !!targetElement.members,
                targetElement.members?.length || 0,
              );

              // For relations, resolve member ways and find connections
              let displayElements: any[] = [];

              if (targetElement.type === "relation" && targetElement.members) {
                debug("Relation has %d members", targetElement.members.length);
                const memberWayIds = new Set<number>();
                for (const member of targetElement.members) {
                  if (member.type === "way") {
                    memberWayIds.add(
                      typeof member.ref === "number"
                        ? member.ref
                        : Number(member.ref),
                    );
                  }
                }

                const memberWays = cachedData.filter(
                  (e: any) =>
                    e.type === "way" &&
                    e.id !== undefined &&
                    memberWayIds.has(Number(e.id)),
                );

                debug(
                  "Found %d/%d member ways in cache",
                  memberWays.length,
                  memberWayIds.size,
                );

                if (memberWays.length > 0) {
                  // For relations, use ALL member ways as initial segments and find connections
                  displayElements = mapService.findConnectedSegmentsForRelation(
                    memberWays,
                    cachedData,
                    targetElement.tags?.name,
                  );
                  debug(
                    "Relation member ways + connected: %d total segments",
                    displayElements.length,
                  );
                } else {
                  displayElements = [targetElement];
                }
              } else if (targetElement.type === "way") {
                // For ways, find connected segments
                displayElements = mapService.findConnectedSegments(
                  targetElement,
                  cachedData,
                );
                debug(
                  "Found %d connected segments for way",
                  displayElements.length,
                );
              } else {
                displayElements = [targetElement];
              }

              if (displayElements.length > 0) {
                debug(
                  "Displaying %d geometry elements",
                  displayElements.length,
                );
                // Display the feature on the map
                mapService.displaySelectedFeature(
                  displayElements,
                  overpassWorker,
                );
                geometryFetched = true;

                // Calculate total distance for the route
                const totalDistance = displayElements.reduce(
                  (sum: number, elem: any) => {
                    if (elem.geometry?.coordinates) {
                      const coords = elem.geometry.coordinates;
                      let dist = 0;
                      for (let i = 1; i < coords.length; i++) {
                        const [lon1, lat1] = coords[i - 1];
                        const [lon2, lat2] = coords[i];
                        dist +=
                          calculateDistanceMiles(lat1, lon1, lat2, lon2) *
                          1609.34; // Convert miles to meters
                      }
                      return sum + dist;
                    }
                    return sum;
                  },
                  0,
                );

                // Start route tracking
                routeService.startRoute({
                  routeName: result.display_name.split(",")[0],
                  routeId: String(result.place_id),
                  totalDistance: totalDistance,
                  geometry: displayElements,
                  centerLat: parseFloat(result.lat),
                  centerLon: parseFloat(result.lon),
                });
                debug(
                  "Started route tracking: %s, %.2f meters",
                  result.display_name,
                  totalDistance,
                );

                closeIsland();
                // The displaySelectedFeature method will handle fitting bounds
                return; // Don't do additional centering
              }
            }
          } catch (err) {
            debug.error("Failed to fetch feature geometry:", err);
            // Fall through to normal centering
          }
        }

        // Clear any previously selected feature if we didn't fetch new geometry
        if (!geometryFetched) {
          mapService.clearSelectedFeature();
        }

        // Check if the result has a bounding box
        if (result.boundingbox && result.boundingbox.length === 4) {
          // Use the provided bounding box
          // Nominatim returns [minLat, maxLat, minLon, maxLon]
          const bounds = {
            minLat: parseFloat(result.boundingbox[0]),
            maxLat: parseFloat(result.boundingbox[1]),
            minLon: parseFloat(result.boundingbox[2]),
            maxLon: parseFloat(result.boundingbox[3]),
          };

          // Check if bounds are reasonable (not too small)
          const latDiff = bounds.maxLat - bounds.minLat;
          const lonDiff = bounds.maxLon - bounds.minLon;

          if (latDiff > 0.0001 && lonDiff > 0.0001) {
            // Use the bounding box if it's reasonable
            mapService.fitBounds(bounds);
          } else {
            // Bounds too small, create a 1-mile box
            const lat = parseFloat(result.lat);
            const lon = parseFloat(result.lon);
            const mileInDegrees = 0.5 / 69; // Rough approximation

            mapService.fitBounds({
              minLat: lat - mileInDegrees,
              maxLat: lat + mileInDegrees,
              minLon: lon - mileInDegrees,
              maxLon: lon + mileInDegrees,
            });
          }
        } else {
          // No bounding box, create a 1-mile box around the point
          const lat = parseFloat(result.lat);
          const lon = parseFloat(result.lon);
          const mileInDegrees = 0.5 / 69; // Rough approximation: 1 degree latitude ≈ 69 miles

          mapService.fitBounds({
            minLat: lat - mileInDegrees,
            maxLat: lat + mileInDegrees,
            minLon: lon - mileInDegrees,
            maxLon: lon + mileInDegrees,
          });
        }
      }
    } finally {
      // Clear busy state
      setSearchBusy(false);
    }
  };

  // Store handleResultPreview in a ref so it's stable
  const handleResultPreviewRef = useRef(handleResultPreview);
  useEffect(() => {
    handleResultPreviewRef.current = handleResultPreview;
  });

  // Note: Search processing events are now handled globally in App.tsx

  // Listen for clicks on search result points on the map
  useEffect(() => {
    const handleSearchResultClick = async (event: Event) => {
      const customEvent = event as CustomEvent;
      debug("Search result point clicked: %o", customEvent.detail);

      const result = customEvent.detail as SearchResult;
      if (!result) {
        debug("ERROR: No result in event detail");
        return;
      }

      // Call handleResultPreview directly with the result
      debug("Calling handleResultPreview with:", result.display_name);
      // Create a fake event since handleResultPreview expects one
      const fakeEvent = { stopPropagation: () => {} } as React.MouseEvent;
      await handleResultPreviewRef.current(result, fakeEvent);
    };

    debug("Adding searchResultPointClicked listener");
    window.addEventListener(
      "searchResultPointClicked",
      handleSearchResultClick,
    );

    return () => {
      debug("Removing searchResultPointClicked listener");
      window.removeEventListener(
        "searchResultPointClicked",
        handleSearchResultClick,
      );
    };
  }, []); // Empty dependency array - only run once

  const getResultIcon = (type: string, result?: any) => {
    // Check for trails and cycleways first (like The Veloway)
    if (
      type === "cycleway" ||
      type === "path" ||
      result?.tags?.route === "bicycle" ||
      result?.tags?.bicycle === "designated" ||
      result?.display_name?.toLowerCase().includes("veloway") ||
      result?.display_name?.toLowerCase().includes("trail")
    ) {
      return "🚴";
    }
    // Simple icon mapping based on result type
    if (type.includes("restaurant") || type.includes("food")) return "🍴";
    if (type.includes("cafe") || type.includes("coffee")) return "☕";
    if (type.includes("park") || type.includes("garden")) return "🌳";
    if (type.includes("shop") || type.includes("store")) return "🛍️";
    if (type.includes("hotel") || type.includes("lodging")) return "🏨";
    if (type.includes("hospital") || type.includes("medical")) return "🏥";
    if (type.includes("school") || type.includes("university")) return "🎓";
    if (type.includes("station") || type.includes("transit")) return "🚉";
    if (type.includes("parking")) return "🅿️";
    if (type.includes("bank") || type.includes("atm")) return "🏦";
    return "📍"; // Default location pin
  };

  // Using shared geo utilities from geoUtils.ts (calculateDistanceMiles, calculateBearing)

  const formatDistance = (miles: number): string => {
    const meters = miles * 1609.34; // Convert miles to meters
    return formatDistanceWithUnit(meters, "imperial");
  };

  return (
    <>
      <div
        className="island-header"
        style={{
          marginBottom: "0",
          padding: "8px",
          gap: "6px",
        }}
      >
        {/* Search icon or spinner */}
        <div
          className="island-icon"
          style={{
            fontSize: "18px",
            flexShrink: 0,
            position: "relative",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            width: "24px",
            height: "24px",
          }}
        >
          {(isSearchingGlobal || isWaiting) && (
            <svg
              width="24"
              height="24"
              viewBox="0 0 24 24"
              style={{
                position: "absolute",
                top: "0",
                left: "0",
                transform: "rotate(-90deg)",
                animation: "spin 1s linear infinite",
              }}
            >
              <circle
                cx="12"
                cy="12"
                r="10"
                fill="none"
                stroke="#60a5fa"
                strokeWidth="2"
                strokeDasharray="47.1 62.8"
                strokeLinecap="round"
              />
            </svg>
          )}
          <span style={{ position: "relative", zIndex: 1, fontSize: "18px" }}>
            🔍
          </span>
        </div>

        {/* Cache grid indicator for local search */}
        {searchDistance === "local" &&
          (() => {
            // Calculate cell size to fit in 24x24px total area
            // Formula: (cellSize * gridSize) + (gap * (gridSize - 1)) + (padding * 2) = 24
            const totalSize = 24;
            const padding = 2;
            const gap = 0.5; // Use smaller gap for better fit
            const availableSize =
              totalSize - padding * 2 - gap * (uiConfig.cacheGridSize - 1);
            const cellSize = availableSize / uiConfig.cacheGridSize;

            return (
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: `repeat(${uiConfig.cacheGridSize}, ${cellSize}px)`,
                  gridTemplateRows: `repeat(${uiConfig.cacheGridSize}, ${cellSize}px)`,
                  gap: `${gap}px`,
                  padding: `${padding}px`,
                  background: "rgba(0, 0, 0, 0.3)",
                  borderRadius: "3px",
                  position: "relative",
                  width: "24px",
                  height: "24px",
                  boxSizing: "border-box",
                  flexShrink: 0,
                }}
                title="Cache coverage for search area"
              >
                {cacheGridState.map((status, index) => (
                  <div
                    key={index}
                    style={{
                      width: "100%",
                      height: "100%",
                      background:
                        status === "cached"
                          ? "rgba(100, 255, 100, 0.5)"
                          : status === "failed"
                            ? "rgba(255, 50, 50, 0.6)"
                            : status === "fetching"
                              ? "rgba(50, 100, 50, 0.7)"
                              : "rgba(120, 120, 120, 0.3)",
                      borderRadius: "1px",
                      transition: "background 0.3s",
                    }}
                  />
                ))}
                {/* Blue dot for current map center position */}
                {(() => {
                  const mapService = window.__bikedash_maps?.["main"];
                  const center = mapService?.getCenter();
                  if (!center) return null;

                  const GRID_SIZE = 0.2;
                  const centerGridLat =
                    Math.round(center.lat / GRID_SIZE) * GRID_SIZE;
                  const centerGridLon =
                    Math.round(center.lon / GRID_SIZE) * GRID_SIZE;

                  const latOffset = center.lat - centerGridLat;
                  const lonOffset = center.lon - centerGridLon;

                  // Grid is always 24x24px total
                  const centerPixel = totalSize / 2;

                  // One cell = 0.2 degrees, so pixels per 0.1 degree = cellSize / 2
                  const pixelsPerTenth = cellSize / 2;

                  const pixelX =
                    centerPixel + (lonOffset / 0.1) * pixelsPerTenth;
                  const pixelY =
                    centerPixel - (latOffset / 0.1) * pixelsPerTenth;

                  return (
                    <div
                      style={{
                        position: "absolute",
                        left: `${pixelX}px`,
                        top: `${pixelY}px`,
                        width: "3px",
                        height: "3px",
                        background: "rgba(0, 150, 255, 0.9)",
                        border: "0.5px solid rgba(255, 255, 255, 0.6)",
                        borderRadius: "50%",
                        transform: "translate(-50%, -50%)",
                        pointerEvents: "none",
                        boxShadow: "0 0 3px rgba(0, 150, 255, 0.8)",
                      }}
                    />
                  );
                })()}
              </div>
            );
          })()}

        {/* Search input as the main content */}
        <input
          type="text"
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          placeholder={
            isSearching || isWaiting
              ? searchDistance === "local"
                ? "Searching bike features..."
                : "Searching..."
              : positionDebouncing
                ? "Updating location..."
                : searchResults.length > 0
                  ? `${searchResults.length} results`
                  : searchDistance === "local"
                    ? "Filter trails & parks..."
                    : "Search places..."
          }
          autoFocus
          style={{
            flex: 1,
            padding: "8px",
            fontSize: "13px",
            background: "rgba(255, 255, 255, 0.1)",
            border: "1px solid rgba(255, 255, 255, 0.2)",
            borderRadius: "6px",
            color: "var(--text-primary)",
            outline: "none",
          }}
          onFocus={(e) => e.target.select()}
        />

        {/* Distance toggle buttons */}
        <div
          style={{
            display: "flex",
            gap: "2px",
            background: "rgba(255, 255, 255, 0.1)",
            borderRadius: "6px",
            padding: "2px",
            flexShrink: 0,
          }}
        >
          {(["local", "25mi", "∞"] as SearchDistance[]).map((dist) => (
            <button
              key={dist}
              onClick={() => setSearchDistance(dist)}
              style={{
                padding: "6px",
                fontSize: "11px",
                background:
                  searchDistance === dist
                    ? positionDebouncing && dist === "local"
                      ? "rgba(255, 200, 0, 0.3)" // Yellow tint when position debouncing
                      : "var(--accent)"
                    : "transparent",
                border:
                  searchDistance === dist ? "1px solid var(--accent)" : "none",
                borderRadius: "4px",
                color:
                  searchDistance === dist
                    ? positionDebouncing && dist === "local"
                      ? "rgba(255, 200, 0, 0.9)" // Yellow text when position debouncing
                      : "white"
                    : "var(--text-secondary)",
                cursor: "pointer",
                fontWeight: searchDistance === dist ? 600 : 400,
                transition: "all 0.2s",
                opacity: positionDebouncing && dist === "local" ? 0.8 : 1,
              }}
              title={
                dist === "local"
                  ? positionDebouncing
                    ? "Updating location..."
                    : "Trails & parks within ~10mi"
                  : `Search within ${dist}`
              }
            >
              {dist === "local" ? "🚴 10mi" : dist}
            </button>
          ))}
        </div>

        {/* Close button */}
        <button
          className="island-close-btn"
          onClick={() => {
            isExplicitCloseRef.current = true;
            searchService.clearQuery(); // Clear query in service (will clear markers too)
            searchService.setDistance("local"); // Reset distance in service
            setSearchQuery(""); // Clear local state
            setSearchResults([]); // Clear local results
            setSearchDistance("local"); // Reset to local search
            persistentSearchState.query = ""; // Clear persisted query
            persistentSearchState.results = []; // Clear persisted results
            persistentSearchState.distance = "local"; // Reset persisted distance
            closeIsland();
          }}
          style={{
            background: "none",
            border: "none",
            color: "var(--text-secondary)",
            fontSize: "18px",
            cursor: "pointer",
            padding: "2px",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            flexShrink: 0,
          }}
        >
          ×
        </button>
      </div>

      <div
        className="island-details"
        style={{
          padding: "10px",
          paddingTop: "0px",
          paddingBottom: "0px",
          display: "flex",
          flexDirection: "column",
          height: "calc(100% - 50px)", // Account for header height
          overflow: "hidden",
        }}
      >
        {/* Error message */}
        {error && (
          <div
            style={{
              color: "var(--error)",
              fontSize: "12px",
              marginBottom: "8px",
              padding: "4px 8px",
              background: "rgba(255, 0, 0, 0.1)",
              borderRadius: "4px",
              flexShrink: 0,
            }}
          >
            {error}
          </div>
        )}

        {/* Search results */}
        <div
          ref={scrollContainerRef}
          style={{
            paddingTop: "4px",
            paddingBottom: "4px",
            flex: 1,
            overflowY: "auto",
            overflowX: "hidden",
            WebkitOverflowScrolling: "touch", // For smooth scrolling on iOS
          }}
        >
          {/* Combined and sorted results (saved routes + search results) */}
          {(() => {
            const mapService = window.__bikedash_maps?.["main"];
            const mapCenter = mapService?.getCenter();

            // Filter saved routes by query if it exists
            const filteredSavedRoutes = searchQuery
              ? savedRoutes.filter((route) =>
                  route.name?.toLowerCase().includes(searchQuery.toLowerCase()),
                )
              : savedRoutes;

            // Further filter by distance if it's a search with query
            const displayedSavedRoutes =
              searchQuery && searchDistance === "local"
                ? filteredSavedRoutes.filter((route) => {
                    if (!mapCenter) return true;
                    const distance = calculateDistanceMiles(
                      mapCenter.lat,
                      mapCenter.lon,
                      route.centerLat,
                      route.centerLon,
                    );
                    // Show routes within ~25 miles for local search
                    return distance < 25;
                  })
                : filteredSavedRoutes;

            // Combine saved routes and search results
            const combinedResults: Array<{
              type: "saved" | "search";
              data: any;
              distance: number | null;
            }> = [];

            // Add saved routes
            displayedSavedRoutes.forEach((savedRoute) => {
              const distance = mapCenter
                ? calculateDistanceMiles(
                    mapCenter.lat,
                    mapCenter.lon,
                    savedRoute.centerLat,
                    savedRoute.centerLon,
                  )
                : null;
              combinedResults.push({
                type: "saved",
                data: savedRoute,
                distance,
              });
            });

            // Add search results (or nearby trails if no query)
            const resultsToShow =
              searchResults.length > 0 ? searchResults : nearbyTrails;

            // Create a set of saved route IDs for deduplication
            const savedRouteIds = new Set(
              displayedSavedRoutes.map((route) => route.id),
            );

            resultsToShow.forEach((result) => {
              // Skip if this result matches a saved route
              const resultId = String(result.place_id);
              if (savedRouteIds.has(resultId)) {
                return;
              }

              const resultLat = parseFloat(result.lat);
              const resultLon = parseFloat(result.lon);
              const distance = mapCenter
                ? calculateDistanceMiles(
                    mapCenter.lat,
                    mapCenter.lon,
                    resultLat,
                    resultLon,
                  )
                : null;
              combinedResults.push({ type: "search", data: result, distance });
            });

            // Sort by distance
            combinedResults.sort((a, b) => {
              if (a.distance === null) return 1;
              if (b.distance === null) return -1;
              return a.distance - b.distance;
            });

            if (combinedResults.length === 0) {
              return (
                <div
                  style={{
                    color: "var(--text-secondary)",
                    fontSize: "12px",
                    textAlign: "center",
                    padding: "20px",
                  }}
                >
                  {isSearching || isWaiting ? (
                    <div style={{ color: "#60a5fa" }}>
                      {searchDistance === "local"
                        ? "Searching nearby bike-related features..."
                        : "Searching..."}
                    </div>
                  ) : searchQuery ? (
                    searchDistance === "local" ? (
                      <div>
                        <div style={{ marginBottom: "8px" }}>
                          No results found nearby
                        </div>
                        <div
                          style={{
                            fontSize: "11px",
                            color: "var(--text-tertiary)",
                            display: "flex",
                            gap: "4px",
                            alignItems: "center",
                            justifyContent: "center",
                          }}
                        >
                          <span>Try searching a larger area:</span>
                          <button
                            onClick={() => setSearchDistance("25mi")}
                            style={{
                              background: "rgba(96, 165, 250, 0.2)",
                              border: "1px solid rgba(96, 165, 250, 0.3)",
                              borderRadius: "4px",
                              color: "#60a5fa",
                              padding: "2px 6px",
                              fontSize: "11px",
                              cursor: "pointer",
                              fontWeight: 600,
                            }}
                          >
                            25mi
                          </button>
                          <span>or</span>
                          <button
                            onClick={() => setSearchDistance("∞")}
                            style={{
                              background: "rgba(96, 165, 250, 0.2)",
                              border: "1px solid rgba(96, 165, 250, 0.3)",
                              borderRadius: "4px",
                              color: "#60a5fa",
                              padding: "2px 6px",
                              fontSize: "11px",
                              cursor: "pointer",
                              fontWeight: 600,
                            }}
                          >
                            ∞
                          </button>
                        </div>
                      </div>
                    ) : (
                      "No results found"
                    )
                  ) : searchDistance === "local" ? (
                    "Nearby trails and cycling routes"
                  ) : (
                    "Type to search for places"
                  )}
                </div>
              );
            }

            return (
              <>
                {combinedResults.map((item, index) => {
                  if (item.type === "saved") {
                    const savedRoute = item.data;
                    const routeLat = savedRoute.centerLat;
                    const routeLon = savedRoute.centerLon;

                    // Get map center distance and bearing
                    const mapService = window.__bikedash_maps?.["main"];
                    const mapCenter = mapService?.getCenter();
                    const distanceToCenter = mapCenter
                      ? calculateDistanceMiles(
                          mapCenter.lat,
                          mapCenter.lon,
                          routeLat,
                          routeLon,
                        )
                      : null;
                    const bearingFromCenter = mapCenter
                      ? calculateBearing(
                          mapCenter.lat,
                          mapCenter.lon,
                          routeLat,
                          routeLon,
                        )
                      : null;

                    // Get GPS distance if available
                    const distanceToGPS = currentGpsPosition
                      ? calculateDistanceMiles(
                          currentGpsPosition.lat,
                          currentGpsPosition.lng,
                          routeLat,
                          routeLon,
                        )
                      : null;

                    return (
                      <div
                        key={savedRoute.id}
                        style={{
                          width: "100%",
                          marginBottom: "2px",
                          display: "flex",
                          gap: "4px",
                        }}
                      >
                        <button
                          onClick={() => {
                            const mapService = window.__bikedash_maps?.["main"];
                            if (mapService) {
                              mapService.setMode("free");
                              mapService.centerOnPosition({
                                lat: routeLat,
                                lng: routeLon,
                                timestamp: Date.now(),
                              });
                            }
                          }}
                          style={{
                            flex: 1,
                            padding: "6px",
                            background: "rgba(255, 215, 0, 0.1)",
                            border: "1px solid rgba(255, 215, 0, 0.2)",
                            borderRadius: "6px 0 0 6px",
                            color: "var(--text-primary)",
                            cursor: "pointer",
                            textAlign: "left",
                            display: "flex",
                            alignItems: "flex-start",
                            gap: "8px",
                            transition: "background 0.2s",
                          }}
                          onMouseEnter={(e) => {
                            e.currentTarget.style.background =
                              "rgba(255, 215, 0, 0.2)";
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.background =
                              "rgba(255, 215, 0, 0.1)";
                          }}
                        >
                          <span style={{ fontSize: "16px", flexShrink: 0 }}>
                            ⭐
                          </span>
                          <div style={{ flex: 1, minWidth: 0 }}>
                            <div
                              style={{
                                fontSize: "var(--font-size-sm)",
                                fontWeight: 600,
                                marginBottom: "2px",
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                                whiteSpace: "nowrap",
                              }}
                            >
                              {savedRoute.name}
                            </div>
                            <div
                              style={{
                                fontSize: "9px",
                                color: "var(--text-tertiary)",
                                display: "flex",
                                gap: "6px",
                                alignItems: "center",
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                                whiteSpace: "nowrap",
                              }}
                            >
                              {distanceToCenter !== null &&
                                bearingFromCenter !== null && (
                                  <span
                                    style={{
                                      display: "flex",
                                      alignItems: "center",
                                      gap: "2px",
                                      flexShrink: 0,
                                    }}
                                  >
                                    <span
                                      style={{
                                        display: "inline-block",
                                        transform: `rotate(${bearingFromCenter}deg)`,
                                        fontSize: "10px",
                                      }}
                                    >
                                      ↑
                                    </span>
                                    {formatDistance(distanceToCenter)}
                                  </span>
                                )}
                              {distanceToGPS !== null && (
                                <span style={{ flexShrink: 0 }}>
                                  <span
                                    style={{
                                      color: "#60a5fa",
                                      fontSize: "12px",
                                    }}
                                  >
                                    ●
                                  </span>{" "}
                                  {formatDistance(distanceToGPS)}
                                </span>
                              )}
                              <span
                                style={{
                                  color: "var(--text-secondary)",
                                  flexShrink: 0,
                                }}
                              >
                                {(savedRoute.totalDistance / 1000).toFixed(1)}{" "}
                                km
                              </span>
                            </div>
                          </div>
                        </button>
                        <button
                          onClick={async () => {
                            closeIsland();
                            const mapService = window.__bikedash_maps?.["main"];
                            if (mapService && savedRoute.geometry) {
                              mapService.setMode("free");
                              mapService.setRotation(0);
                              const overpassWorker = getOverpassWorkerService();
                              mapService.displaySelectedFeature(
                                savedRoute.geometry,
                                overpassWorker,
                              );

                              // Activate the route
                              routeService.startRoute({
                                routeName: savedRoute.name,
                                routeId: savedRoute.id,
                                totalDistance: savedRoute.totalDistance || 0,
                                geometry: savedRoute.geometry,
                                centerLat: savedRoute.centerLat,
                                centerLon: savedRoute.centerLon,
                              });
                            }
                          }}
                          style={{
                            padding: "6px 10px",
                            background: "rgba(255, 215, 0, 0.1)",
                            border: "1px solid rgba(255, 215, 0, 0.2)",
                            borderLeft: "none",
                            borderRadius: "0 6px 6px 0",
                            cursor: "pointer",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            transition: "background 0.2s",
                          }}
                          onMouseEnter={(e) => {
                            e.currentTarget.style.background =
                              "rgba(96, 165, 250, 0.2)";
                            const svg = e.currentTarget.querySelector("path");
                            if (svg) svg.setAttribute("stroke", "#60a5fa");
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.background =
                              "rgba(255, 215, 0, 0.1)";
                            const svg = e.currentTarget.querySelector("path");
                            if (svg) svg.setAttribute("stroke", "#ffd700");
                          }}
                          title="Activate Route"
                        >
                          <svg
                            width="24"
                            height="20"
                            viewBox="0 0 24 20"
                            fill="none"
                          >
                            <path
                              d="M 2,14 C 6,14 6,6 10,6 C 14,6 14,14 18,14 C 22,14 22,6 22,6"
                              stroke="#ffd700"
                              strokeWidth="2.5"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              fill="none"
                            />
                          </svg>
                        </button>
                      </div>
                    );
                  } else {
                    // Search result
                    const result = item.data;
                    const resultLat = parseFloat(result.lat);
                    const resultLon = parseFloat(result.lon);

                    // Get map center distance and bearing
                    const mapService = window.__bikedash_maps?.["main"];
                    const mapCenter = mapService?.getCenter();
                    const distanceToCenter = mapCenter
                      ? calculateDistanceMiles(
                          mapCenter.lat,
                          mapCenter.lon,
                          resultLat,
                          resultLon,
                        )
                      : null;
                    const bearingFromCenter = mapCenter
                      ? calculateBearing(
                          mapCenter.lat,
                          mapCenter.lon,
                          resultLat,
                          resultLon,
                        )
                      : null;

                    // Get GPS distance if available
                    const distanceToGPS = currentGpsPosition
                      ? calculateDistanceMiles(
                          currentGpsPosition.lat,
                          currentGpsPosition.lng,
                          resultLat,
                          resultLon,
                        )
                      : null;

                    return (
                      <div
                        key={result.place_id}
                        style={{
                          width: "100%",
                          marginBottom: "2px",
                          display: "flex",
                          gap: "4px",
                          overflow: "hidden",
                        }}
                      >
                        <button
                          onClick={() => handleResultPan(result)}
                          style={{
                            flex: 1,
                            minWidth: 0,
                            padding: "6px",
                            background: "rgba(255, 255, 255, 0.05)",
                            border: "1px solid rgba(255, 255, 255, 0.1)",
                            borderRadius: "6px",
                            color: "var(--text-primary)",
                            cursor: "pointer",
                            textAlign: "left",
                            display: "flex",
                            alignItems: "flex-start",
                            gap: "8px",
                            transition: "background 0.2s",
                          }}
                          onMouseEnter={(e) => {
                            e.currentTarget.style.background =
                              "rgba(255, 255, 255, 0.1)";
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.background =
                              "rgba(255, 255, 255, 0.05)";
                          }}
                        >
                          <span style={{ fontSize: "16px", flexShrink: 0 }}>
                            {getResultIcon(result.type, result)}
                          </span>
                          <div style={{ flex: 1, minWidth: 0 }}>
                            <div
                              style={{
                                fontSize: "var(--font-size-sm)",
                                fontWeight: 600,
                                marginBottom: "2px",
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                                whiteSpace: "nowrap",
                              }}
                            >
                              {result.display_name.split(",")[0]}
                            </div>
                            <div
                              style={{
                                fontSize: "9px",
                                color: "var(--text-tertiary)",
                                display: "flex",
                                gap: "6px",
                                alignItems: "center",
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                                whiteSpace: "nowrap",
                              }}
                            >
                              {distanceToCenter !== null &&
                                bearingFromCenter !== null && (
                                  <span
                                    style={{
                                      display: "flex",
                                      alignItems: "center",
                                      gap: "2px",
                                      flexShrink: 0,
                                    }}
                                  >
                                    <span
                                      style={{
                                        display: "inline-block",
                                        transform: `rotate(${bearingFromCenter}deg)`,
                                        fontSize: "10px",
                                      }}
                                    >
                                      ↑
                                    </span>
                                    {formatDistance(distanceToCenter)}
                                  </span>
                                )}
                              {distanceToGPS !== null && (
                                <span style={{ flexShrink: 0 }}>
                                  <span
                                    style={{
                                      color: "#60a5fa",
                                      fontSize: "12px",
                                    }}
                                  >
                                    ●
                                  </span>{" "}
                                  {formatDistance(distanceToGPS)}
                                </span>
                              )}
                              <span
                                style={{
                                  color: "var(--text-secondary)",
                                  overflow: "hidden",
                                  textOverflow: "ellipsis",
                                  whiteSpace: "nowrap",
                                }}
                              >
                                {result.display_name}
                              </span>
                            </div>
                          </div>
                        </button>
                        <button
                          onClick={(e) => {
                            // Only activate as route for actual paths/trails/cycleways
                            debug(
                              "Button clicked - type=%s, tags=%o, display_name=%s",
                              result.type,
                              result.tags,
                              result.display_name,
                            );
                            const isRoute =
                              result.type === "cycleway" ||
                              result.type === "path" ||
                              result.tags?.route === "bicycle" ||
                              result.tags?.bicycle === "designated" ||
                              result.tags?.highway === "cycleway" ||
                              result.tags?.highway === "path" ||
                              result.display_name
                                ?.toLowerCase()
                                .includes("trail") ||
                              result.display_name
                                ?.toLowerCase()
                                .includes("veloway");

                            debug("isRoute=%s", isRoute);

                            if (isRoute) {
                              // For routes/trails, activate route preview
                              handleResultPreview(result, e);
                            } else {
                              // For everything else (parks, cities, POIs), show as place
                              handleResultPan(result);
                            }
                          }}
                          style={{
                            width: "32px",
                            height: "auto",
                            padding: "6px 4px",
                            background: "rgba(255, 255, 255, 0.05)",
                            border: "1px solid rgba(255, 255, 255, 0.1)",
                            borderRadius: "6px",
                            cursor: "pointer",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            transition: "background 0.2s",
                            flexShrink: 0,
                          }}
                          onMouseEnter={(e) => {
                            e.currentTarget.style.background =
                              "rgba(96, 165, 250, 0.2)";
                            const svg = e.currentTarget.querySelector("path");
                            if (svg) svg.setAttribute("stroke", "#93c5fd");
                            const text = e.currentTarget.querySelector("text");
                            if (text) text.setAttribute("fill", "#93c5fd");
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.background =
                              "rgba(255, 255, 255, 0.05)";
                            const svg = e.currentTarget.querySelector("path");
                            if (svg) svg.setAttribute("stroke", "#60a5fa");
                            const text = e.currentTarget.querySelector("text");
                            if (text) text.setAttribute("fill", "#60a5fa");
                          }}
                          title={(() => {
                            const isRoute =
                              result.type === "cycleway" ||
                              result.type === "path" ||
                              result.tags?.route === "bicycle" ||
                              result.tags?.bicycle === "designated" ||
                              result.tags?.highway === "cycleway" ||
                              result.tags?.highway === "path" ||
                              result.display_name
                                ?.toLowerCase()
                                .includes("trail") ||
                              result.display_name
                                ?.toLowerCase()
                                .includes("veloway");
                            return isRoute
                              ? "Activate Route"
                              : "Go to Location";
                          })()}
                        >
                          {(() => {
                            const isRoute =
                              result.type === "cycleway" ||
                              result.type === "path" ||
                              result.tags?.route === "bicycle" ||
                              result.tags?.bicycle === "designated" ||
                              result.tags?.highway === "cycleway" ||
                              result.tags?.highway === "path" ||
                              result.display_name
                                ?.toLowerCase()
                                .includes("trail") ||
                              result.display_name
                                ?.toLowerCase()
                                .includes("veloway");

                            return isRoute ? (
                              <svg
                                width="24"
                                height="20"
                                viewBox="0 0 24 20"
                                fill="none"
                              >
                                <path
                                  d="M 2,14 C 6,14 6,6 10,6 C 14,6 14,14 18,14 C 22,14 22,6 22,6"
                                  stroke="#60a5fa"
                                  strokeWidth="2.5"
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                  fill="none"
                                />
                              </svg>
                            ) : (
                              <svg
                                width="24"
                                height="20"
                                viewBox="0 0 24 20"
                                fill="none"
                              >
                                <text
                                  x="12"
                                  y="15"
                                  fontSize="16"
                                  textAnchor="middle"
                                  fill="#60a5fa"
                                >
                                  📍
                                </text>
                              </svg>
                            );
                          })()}
                        </button>
                      </div>
                    );
                  }
                })}
              </>
            );
          })()}
        </div>
      </div>
    </>
  );
};
